<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'rentalServices',
    path: '/rental-services',
    component: 'RentalServices',
    title: () => gettext('Rental services'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RentalServices',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const gridName = ref('rentalServices');
  const selectedItems = ref([]);
  const dsParams = ref({
    skip_archived: true,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'contract_revision',
      name: $fn.value.pgettext('Contract', 'Contract number'),
      sortPath: 'contract_revision.contract_number',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_revision.contract_number',
              source: 'row',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            skipExport: true,
            params: {
              id: 'contract_id',
            },
          },
        ],
      },
    },
    {
      id: 'contract_type',
      field: 'contract_revision',
      name: $fn.value.pgettext('RentalServices', 'Type'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_type',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapField: 'contract_revision.contract_type',
            variantMap: {
              rental: 'contract-type--rental',
              vendor: 'contract-type--vendor',
              rental_agreement: 'contract-type--rental_agreement',
              service: 'contract-type--service',
              parking: 'contract-type--parking',
              unknown: 'contract-type--unknown',
            },
          },
        ],
      },
    },
    {
      id: 'contract_status',
      field: 'contract_revision',
      name: $fn.value.pgettext('RentalServices', 'Status'),
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'status',
              source: 'value',
            },
          },
          {
            name: 'gettext',
            context: 'ContractStatus',
            conversion: 'StartCase',
            filterInput: true,
          },
          {
            name: 'badge',
            variantMapField: 'contract_revision.status',
            variantMap: {
              draft: 'light',
              approved: 'success',
              signed: 'success',
              active: 'primary',
              ended: 'warning',
              terminated: 'warning',
              rescinded: 'danger',
              archived: 'light',
            },
          },
        ],
      },
    },
    {
      field: 'rental',
      name: $fn.value.pgettext('RentalServices', 'Rental'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.name }}</strong>\n\
    {{~#if $value.house_number }} | {{ $value.house_number }}{{/if }}\n\
    {{~#if $value.area }} ({{$value.area}}{{$value.unit_of_area}}){{/if }}\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'rental_id',
            },
          },
        ],
      },
    },
    {
      field: 'company',
      name: $fn.value.pgettext('Contract', 'Company'),
      sortPath: 'company.name',
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'company.id',
            },
          },
        ],
      },
    },
    {
      field: 'counterparty',
      name: $fn.value.pgettext('Contract', 'Counterparty'),
      sortPath: 'counterparty.name',
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'counterparty.id',
            },
          },
        ],
      },
    },
    {
      field: 'meter',
      name: $fn.value.pgettext('MeterConsumptionBrowser', 'Meter'),
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong>{{ $value.serial_number }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'meter',
            navigateOnIconClick: true,
            params: {
              id: 'meter.id',
            },
          },
        ],
      },
    },
    {
      field: 'valid_from',
      name: $fn.value.pgettext('RentalServices', 'Valid From'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'valid_to',
      name: $fn.value.pgettext('RentalServices', 'Valid To'),
      formatter: {
        name: 'date',
      },
    },
    {
      field: 'meter_factor',
      name: $fn.value.pgettext('RentalServices', 'Meter factor'),
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'name',
      name: $fn.value.pgettext('ContractService', 'Name'),
      type: 'command',
    },
    {
      field: 'intermediated_service',
      name: $fn.value.pgettext('ContractService', 'Intermediated service'),
      formatter: 'checkbox',
    },
    {
      field: 'non_billable',
      name: $fn.value.pgettext('ContractService', 'Non-billable'),
      formatter: 'checkbox',
    },
    {
      field: 'billing_unit',
      name: $fn.value.pgettext('ContractService', 'Billing unit'),
    },
    {
      field: 'tariff_code',
      name: $fn.value.pgettext('ContractService', 'Taridd code'),
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('integro_api/browse_rental_service', {
      parameters: () => ({ ...dsParams.value, date: $fn.value.today() }),
      assignUniqueId: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'RentalServices.dsParams' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Rental services'),
      icon: $config.value.rental.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_y7ewetbql_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_gco3qwizc_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_reloadGrid_hma5zlu3l_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_2248226175642056_close_mw7vpg807_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_2248226175642056_showModal_tl84zwix6_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalService',
      props: {
        modal: true,
        oid: $event.data.data.originalId || $event.data.data.id,
      },
    };
  };
  const inst_evh_2248226175642056_command_10q5j3cum_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'name';
  };
  const act_7172922356531116_reloadGrid_igfxw9v05_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_7172922356531116_close_fuz4vsha4_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_7172922356531116_showModal_qm16ngk28_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BulkRentalServiceEditor',
      props: {
        items: selectedItems.value,
      },
    };
  };

  const act_7315092382398562_closeModal_y7ewetbql =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_y7ewetbql_getActionArgs,
    });
  const inst_evh_7315092382398562_close_oiro3wozc =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_y7ewetbql,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_oiro3wozc],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_dmmjtj8zo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_umigh76yf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_dmmjtj8zo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_umigh76yf],
    event: 'reload',
  });
  const act_2248226175642056_setData_gco3qwizc = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_gco3qwizc_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_a55kqub4o =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_gco3qwizc,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_a55kqub4o],
      event: 'selectedRowsChanged',
    });
  const act_2248226175642056_reloadGrid_hma5zlu3l =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2248226175642056_reloadGrid_hma5zlu3l_getActionArgs,
    });
  const inst_evh_2248226175642056_close_mw7vpg807 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_reloadGrid_hma5zlu3l,
      event: 'close',
      when: inst_evh_2248226175642056_close_mw7vpg807_getWhen,
      displayName: 'reloadGrid',
    });
  const act_2248226175642056_showModal_tl84zwix6 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2248226175642056_showModal_tl84zwix6_getActionArgs,
      events: [inst_evh_2248226175642056_close_mw7vpg807],
    });
  const inst_evh_2248226175642056_command_10q5j3cum =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_showModal_tl84zwix6,
      event: 'command',
      when: inst_evh_2248226175642056_command_10q5j3cum_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_2248226175642056_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_command_10q5j3cum],
    event: 'command',
  });
  const act_7172922356531116_reloadGrid_igfxw9v05 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7172922356531116_reloadGrid_igfxw9v05_getActionArgs,
    });
  const inst_evh_7172922356531116_close_fuz4vsha4 =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_reloadGrid_igfxw9v05,
      event: 'close',
      when: inst_evh_7172922356531116_close_fuz4vsha4_getWhen,
      displayName: 'reloadGrid',
    });
  const act_7172922356531116_showModal_qm16ngk28 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7172922356531116_showModal_qm16ngk28_getActionArgs,
      events: [inst_evh_7172922356531116_close_fuz4vsha4],
    });
  const inst_evh_7172922356531116_click_l7g007d4y =
    new actionTypes.EventHandlerImpl({
      action: act_7172922356531116_showModal_qm16ngk28,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_7172922356531116_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7172922356531116_click_l7g007d4y],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_oiro3wozc,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_umigh76yf,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_a55kqub4o,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2248226175642056_close_mw7vpg807,
    inst_evh_2248226175642056_command_10q5j3cum,
    inst_evh_2248226175642056_command,
    inst_evh_7172922356531116_close_fuz4vsha4,
    inst_evh_7172922356531116_click_l7g007d4y,
    inst_evh_7172922356531116_click,
  };

  const evh_7315092382398562_close_oiro3wozc = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_oiro3wozc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_umigh76yf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_umigh76yf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_a55kqub4o = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_a55kqub4o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_close_mw7vpg807 = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_close_mw7vpg807.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command_10q5j3cum = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command_10q5j3cum.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_command = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_close_fuz4vsha4 = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_close_fuz4vsha4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click_l7g007d4y = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click_l7g007d4y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7172922356531116_click = async (event, scope) => {
    return await actions.inst_evh_7172922356531116_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RentalServices"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpDataGrid
      :name="gridName"
      :headerTitle="pgettext('RentalServices', 'Rental services')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('filename', 'rental-services')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
      @command="evh_2248226175642056_command($event, {})"
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="skip_archived"
              :label="pgettext('RentalServices', 'Skip archived items')"
              inline
              v-model="dsParams.skip_archived"
            >
            </ItpFormCheckbox>
          </div>
          <div class="border-start ps-3 d-flex gap-2">
            <ItpButton
              :disabled="!selectedItems || !selectedItems.length"
              :icon="$config.contract.createContractIcon"
              :text="pgettext('RentalServices', 'Bulk edit...')"
              size="sm"
              variant="light"
              @click="evh_7172922356531116_click($event, {})"
            >
            </ItpButton>
          </div>
        </div>
      </template>
    </ItpDataGrid>
  </ItpPage>
</template>
